<template>
    <div class="profile-header">

        <!-- Custom component path — rendering mechanism TBD -->
        <template v-if="customCId">
            <!-- TODO: custom component rendering -->
        </template>

        <template v-else>
            <!-- Monogram -->
            <div v-if="headerData.monogram" class="profile-avatar">
                {{ headerData.monogram }}
            </div>

            <!-- Identity info -->
            <div class="profile-info">
                <div v-if="headerData.headline" class="profile-name">
                    {{ headerData.headline }}
                </div>

                <div v-if="headerData.meta && headerData.meta.length" class="profile-meta">
                    <span
                        v-for="item in headerData.meta"
                        :key="item.text"
                        class="meta-item"
                    >
                        <Icon :name="item.icon" color="inherit" />
                        {{ item.text }}
                    </span>
                </div>

                <div v-if="headerData.badges && headerData.badges.length" class="profile-badges">
                    <span
                        v-for="badge in headerData.badges"
                        :key="badge.label"
                        :class="['badge', `badge--${badge.type}`]"
                    >
                        {{ badge.label }}
                    </span>
                </div>
            </div>

            <!-- Actions -->
            <div class="profile-actions">
                <!-- Pinned actions — populated externally in a future ticket -->
                <div class="profile-actions-pinned"></div>

                <!-- Dropdown trigger -->
                <button class="profile-actions-btn" @click.stop="toggleActions">
                    <Icon name="fa-solid fa-ellipsis-vertical" color="inherit" />
                </button>

                <!-- Dropdown panel -->
                <div v-if="actionsOpen" class="profile-actions-dropdown">
                    <!-- actions wired here in a future ticket -->
                </div>
            </div>
        </template>

    </div>
</template>

<script>
import { componentStore } from '@common/componentStore/componentStore';
import Icon from '@common/icon/Icon.vue';

let profileHeaderConfig = {
    cId: 'profileHeader',
    cType: 'profile-header',
    cTitle: 'Profile Header',
    showInCmpList: false,
};

let profileHeaderCmp = {
    name: 'profile-header',
    components: { Icon },
    props: {
        psp: { type: String, required: true },
        recordId: { type: [String, Number], required: true },
        customCId: { type: String, default: null },
        headerConfig: { type: Object, required: true },
    },
    data() {
        return {
            actionsOpen: false,
        };
    },
    computed: {
        headerData() {
            if (!this.headerConfig || !this.recordId) return {};
            const rec = this.recordStore.records?.[this.psp]?.[this.recordId] ?? {};
            const get = (field) => rec[field] ?? null;

            /* This handles monogram, swap out when monogram component is built */
            const firstField = this.headerConfig.monogram?.[0]?.field;
            const lastField = this.headerConfig.monogram?.[1]?.field;
            const monogram = (firstField && lastField)
                ? ((get(firstField)?.[0] ?? '') + (get(lastField)?.[0] ?? '')).toUpperCase() || null
                : null;
            /* ------------------------------------------------------------------ */

            return {
                headline: this.headerConfig.headline?.field ? get(this.headerConfig.headline.field) : null,
                monogram,
                meta: (this.headerConfig.meta ?? [])
                    .map((item) => ({ icon: item.icon, text: get(item.field) }))
                    .filter((item) => item.text),
                badges: (this.headerConfig.badges ?? []).map((item) => ({
                    label: item.label,
                    type: item.type, // temp hardcoded solution: Explore more how this will work.
                })),
            };
        },
    },
    methods: {
        toggleActions() {
            this.actionsOpen = !this.actionsOpen;
        },
        closeActions() {
            this.actionsOpen = false;
        },
    },
    mounted() {
        // this can be changed to syncfusion dropdown now
        this._closeOnOutsideClick = (e) => {
            if (!this.$el.contains(e.target)) this.closeActions();
        };
        document.addEventListener('click', this._closeOnOutsideClick);
    },
    // remove if swap out action dropdown for syncfusion
    beforeUnmount() {
        document.removeEventListener('click', this._closeOnOutsideClick);
    },
};

componentStore.initComponent(profileHeaderCmp, profileHeaderConfig);
export default profileHeaderCmp;
</script>

<style scoped>
.profile-header {
    background: linear-gradient(135deg, var(--surface-default-primary) 0%, #0A66A6 100%);
    padding: var(--space-7) var(--space-8);
    display: flex;
    align-items: flex-start;
    gap: var(--space-6);
    flex-shrink: 0;
    position: relative;
}

/* Decorative background circles */
.profile-header::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
    pointer-events: none;
}
.profile-header::before {
    content: '';
    position: absolute;
    right: 60px;
    bottom: -60px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.03);
    pointer-events: none;
}

/* Monogram */
.profile-avatar {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: var(--primary-500);
    color: var(--text-color-inverse);
    font-size: var(--text-size-h2);
    font-weight: var(--text-weight-bold);
    display: flex;
    align-items: center;
    align-self: center;
    justify-content: center;
    flex-shrink: 0;
    border: 2px solid rgba(255, 255, 255, 0.25);
    letter-spacing: 0.02em;
}

/* Identity block */
.profile-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
}
.profile-name {
    font-size: var(--text-size-h2);
    font-weight: var(--text-weight-bold);
    color: var(--text-color-inverse);
    line-height: 1.2;
}
.profile-meta {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    flex-wrap: wrap;
}
.meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-size-caption);
    color: rgba(255, 255, 255, 0.75);
}
.meta-item i {
    margin-right: var(--space-1);
}
.profile-badges {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    flex-wrap: wrap;
}

/* Badges */
.badge {
    padding: var(--space-2) var(--space-4);
    border-radius: 100px;
    font-size: var(--text-size-caption);
    font-weight: 600;
    letter-spacing: 0.05rem;
    text-transform: uppercase;
}
.badge--active  { background: rgba(46,125,50,0.25);  color: #81c784; border: 1px solid rgba(46,125,50,0.4); }
.badge--cleared { background: rgba(255,255,255,0.12); color: rgba(255,255,255,0.85); border: 1px solid rgba(255,255,255,0.2); }
.badge--warning { background: rgba(245,124,0,0.25);  color: #ffb74d; border: 1px solid rgba(245,124,0,0.4); }

/* Actions */
.profile-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
    padding-top: var(--space-1);
    position: relative;
    z-index: 1;
}
.profile-actions-pinned {
    display: flex;
    align-items: center;
    gap: var(--space-2);
}
.profile-actions-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: background 0.2s ease;
}
.profile-actions-btn:hover { background: rgba(255, 255, 255, 0.15); }
.profile-actions-dropdown {
    position: absolute;
    top: calc(100% + var(--space-2));
    right: 0;
    min-width: 160px;
    background: var(--surface-default);
    border: 1px solid var(--border-color-default);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-2);
    z-index: var(--z-dropdown);
    padding: var(--space-2) 0;
}
</style>
